import { useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { useNotifications } from '../context/NotificationContext';
import { useMarkerActions } from './useMarkerActions';
import { ERROR_CODES, handleError } from '../utils/errorHandling';
import logger from '../utils/logger';
import { markersService } from '../services/supabase';
import { categoriesService } from '../services/supabase/categories.service';
import { eventsService } from '../services/events.service';

// État global pour suivre si un glisser-déposer est en cours
export const isDraggingState = { current: false };

export const useDragAndDrop = () => {
  const { state: appState, dispatch } = useAppContext();
  const { addNotification } = useNotifications();
  const { createCategoryChangeAction } = useMarkerActions();

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, markerId: string) => {
    e.dataTransfer.setData('text/plain', markerId);
    // Activer l'état de glissement global
    isDraggingState.current = true;
    logger.debug('Début du glisser-déposer', { markerId });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Utiliser une bordure highlight pour indiquer l'état drag over
    e.currentTarget.style.setProperty('border', '2px dashed rgba(255, 255, 255, 0.8)');
    e.currentTarget.style.setProperty('box-shadow', '0 0 8px rgba(255, 255, 255, 0.3)');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Restaurer la bordure originale sans affecter la couleur de fond
    e.currentTarget.style.removeProperty('border');
    e.currentTarget.style.removeProperty('box-shadow');
  }, []);

  const handleDragEnd = useCallback((_e: React.DragEvent<HTMLDivElement>) => {
    // Réinitialiser l'état de glissement global
    isDraggingState.current = false;
    logger.debug('Fin du glisser-déposer');
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>, categoryId: string) => {
    e.preventDefault();
    // Nettoyer la bordure highlight sans affecter la couleur de fond originale
    e.currentTarget.style.removeProperty('border');
    e.currentTarget.style.removeProperty('box-shadow');
    // Réinitialiser l'état de glissement global
    isDraggingState.current = false;
    const markerId = e.dataTransfer.getData('text/plain');

    if (!markerId) {
      logger.warn('Tentative de drop sans markerId');
      return;
    }

    const marker = appState.markers.find(m => m.id === markerId);
    if (!marker) {
      const error = handleError({
        code: ERROR_CODES.MARKER.NOT_FOUND,
        message: 'Marqueur non trouvé lors du drag & drop',
        context: { markerId }
      });
      addNotification({
        type: 'warning',
        title: 'Erreur',
        message: error.message
      });
      return;
    }

    // Ne rien faire si on dépose dans la même catégorie
    if (marker.categoryId === categoryId) {
      const error = handleError({
        code: ERROR_CODES.MARKER.CATEGORY_UNCHANGED,
        message: 'Le marqueur est déjà dans cette catégorie',
        context: { markerId, categoryId }
      });
      addNotification({
        type: 'info',
        title: 'Information',
        message: error.message
      });
      return;
    }

    const oldCategory = appState.categories.find(cat => cat.id === marker.categoryId);
    const newCategory = appState.categories.find(cat => cat.id === categoryId);

    if (!newCategory) {
      const error = handleError({
        code: ERROR_CODES.CATEGORY.NOT_FOUND,
        message: 'Catégorie cible non trouvée',
        context: { categoryId }
      });
      addNotification({
        type: 'warning',
        title: 'Erreur',
        message: error.message
      });
      return;
    }

    logger.info('Changement de catégorie', {
      markerId,
      markerName: marker.name || marker.address,
      oldCategory: oldCategory?.name || 'Sans catégorie',
      newCategory: newCategory.name
    });

    const action = createCategoryChangeAction(
      appState.categories.find(c => c.id === marker.categoryId),
      appState.categories.find(c => c.id === categoryId)
    );

    try {
      // Récupérer les données complètes du marqueur
      const existingMarker = await markersService.getById(markerId);
      if (!existingMarker) {
        throw new Error(`Marqueur avec ID ${markerId} non trouvé`);
      }
      
      // Mise à jour dans Supabase avec les données complètes
      await markersService.update(markerId, {
        ...existingMarker,
        categoryId
      });
      
      // Enregistrer le changement de catégorie dans la base de données
      await categoriesService.recordCategoryChange(
        markerId,
        marker.categoryId || '',
        categoryId
      );
      
      // Si la mise à jour Supabase réussit, on met à jour le state local
      const updatedMarker = {
        ...marker,
        categoryId,
        actions: [...(marker.actions || []), action],
        updatedAt: new Date()
      };

      logger.debug('Mise à jour du marqueur:', {
        id: markerId,
        categoryId
      });

      // Mettre à jour le marqueur localement dans l'état
      dispatch({
        type: 'UPDATE_MARKER',
        payload: {
          id: markerId,
          data: updatedMarker
        }
      });

      // Émettre un événement pour rafraîchir uniquement ce marqueur sur la carte
      // Ajouter un flag pour indiquer que c'est un changement de catégorie par glisser-déposer
      // et désactiver complètement le recentrage
      eventsService.emit('REFRESH_MAP_MARKERS', {
        markerId,
        oldCategoryId: marker.categoryId,
        newCategoryId: categoryId,
        preserveZoom: true, // Indiquer qu'il faut préserver le zoom actuel
        disableRecentering: true // Désactiver complètement le recentrage
      });
      
      logger.debug('Événement REFRESH_MAP_MARKERS émis après changement de catégorie', {
        markerId,
        oldCategoryId: marker.categoryId,
        newCategoryId: categoryId
      });

      // Ne pas afficher de notification pour les changements de catégorie
      // Cela évite d'encombrer l'interface avec des notifications système
      // addNotification({
      //   type: 'info',
      //   title: 'Succès',
      //   message: `Marqueur déplacé vers ${newCategory.name}`
      // });
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de la catégorie dans Supabase:', error);
      addNotification({
        type: 'warning',
        title: 'Erreur',
        message: 'Impossible de mettre à jour la catégorie du marqueur'
      });
    }
  }, [dispatch, addNotification, appState.markers, appState.categories, createCategoryChangeAction]);

  return {
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd
  };
};

